import { getTopLeft, getWidth } from 'ol/extent';
import TileLayer from 'ol/layer/Tile';
import LayerTile from 'ol/layer/Tile';
import { OSM, XYZ } from 'ol/source';
import WMTSSource from 'ol/source/WMTS';
import TilegridWMTS from 'ol/tilegrid/WMTS';
import { httpClient, httpConfig } from 'utils/httpClient';
import { WKT } from 'ol/format';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import TileState from 'ol/TileState';
import { fromLonLat, get as getProjection } from 'ol/proj';

const baseURL = process.env.REACT_APP_API_URL;

const projection = getProjection('EPSG:3857');
const projectionExtent = projection?.getExtent();
const size = getWidth(projectionExtent!) / 256;
const resolutions = new Array(19);
const matrixIds = new Array(19);
for (let z = 0; z < 19; ++z) {
  resolutions[z] = size / Math.pow(2, z);
  matrixIds[z] = z;
}

export const makeFeature = (polygon: string) => {
  let formatWkt = new WKT();
  return formatWkt.readFeature(polygon, {
    dataProjection: 'EPSG:3857',
    featureProjection: 'EPSG:3857',
  });
};

export enum SubLayersNames {
  OSM = 'osm',
  TOPO = 'topo',
}

export const onlySubstrLayers = [SubLayersNames.OSM, SubLayersNames.TOPO];

export const osmLayer = new LayerTile({
  source: new OSM(),
  visible: true,
  // @ts-ignore
  name: SubLayersNames.OSM,
});

let osmFrSource = new XYZ({
  url: 'https://a.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png',
});

osmFrSource.setTileLoadFunction(function (tile: any, src: any) {
  let xhr = new XMLHttpRequest();
  xhr.onloadstart = function () {
    xhr.responseType = 'blob';
  };
  xhr.addEventListener('loadend', function (evt) {
    let data = this.response;
    if (data) {
      tile.getImage().src = URL.createObjectURL(data);
      tile.getImage().onload = function () {
        URL.revokeObjectURL(this.src);
      };
    } else {
      tile.setState(TileState.ERROR);
    }
  });
  xhr.addEventListener('error', function () {
    tile.setState(TileState.ERROR);
  });
  xhr.open('GET', src);
  xhr.send();
});

export const openTopoMap = new LayerTile({
  // @ts-ignore
  title: 'OSM',
  name: SubLayersNames.TOPO,
  crossOrigin: 'anonymous',
  type: 'base',
  visible: false,
  source: osmFrSource,
});

export const makeVectorLayer = (feature: any[]) => {
  const vectorLayer = new VectorLayer({
    source: new VectorSource({ features: feature }),
    style: new Style({
      stroke: new Stroke({ color: '#666', width: 5 }),
      image: new Circle({
        radius: 7,
      }),
    }),
  });

  vectorLayer.setZIndex(100);

  return vectorLayer;
};

export const defaultRrojectionExtent = [
  -2.003750834e7, -2.003750834e7, 2.003750834e7, 2.003750834e7,
];

export const searchWktByCoords = async (
  layer: string,
  coords: string[],
  zlevel: string,
  lang: string,
  isLog: boolean
) => {
  const URL = process.env.REACT_APP_API_URL;

  const url = `${URL}/api/${isLog ? 'pv-' : ''}searchbyclick`;
  const [lon, lat] = coords;
  const res = await httpClient.post(
    url,
    new URLSearchParams({
      lang,
      layer,
      zlevel,
      lon,
      lat,
    }),
    httpConfig
  );

  return res.data.data;
};

export const getBorderLayer = (lang: string) => {
  const layerName =
    lang === 'en'
      ? 'border_rb_en'
      : lang === 'by'
      ? 'border_rb_by'
      : 'border_rb';

  let borderWmtsSource = new WMTSSource({
    url: `${baseURL}/mapproxy/service/wmts`,
    crossOrigin: 'anonymous',
    layer: layerName,
    matrixSet: 'mymercator',
    format: 'image/png',
    // @ts-ignore
    projection: projection,
    tileGrid: new TilegridWMTS({
      origin: getTopLeft(projectionExtent || defaultRrojectionExtent),
      resolutions: [
        156543.03392804097, 78271.51696402048, 39135.75848201024,
        19567.87924100512, 9783.93962050256, 4891.96981025128, 2445.98490512564,
        1222.99245256282, 611.49622628141, 305.748113140705, 152.8740565703525,
        76.43702828517625, 38.21851414258813, 19.109257071294063,
        9.554628535647032, 4.777314267823516, 2.388657133911758,
        1.194328566955879,
      ],
      matrixIds: matrixIds,
    }),
    style: 'default',
    wrapX: true,
  });

  return new TileLayer({
    opacity: 1,
    source: borderWmtsSource,
    visible: true,
  });
};

export const checkEqualWkt = (arr: string[]): boolean => {
  const firstItem = arr[0];
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] !== firstItem) {
      return false;
    }
  }

  return true;
};

export const defaultMapCenter = fromLonLat([28.6, 53.9]);
export const defaultMapMinZoom = 5.5;
export const defaultMapMaxZoom = 13;
export const scale_1_50_MapMaxZoom = 17.5;
export const scale_1_50_vector_MapMaxZoom = 18;

export const scale_1_50_vector_clusterZoom = 13.3;

export const borderMaxZoom = 13;

export const mobileZoom = 5.5;
export const desktopZoom = 6.5;

export const mobileExtent = [
  1649079.6237, 5809192.5713, 4535079.626, 8511042.5734,
];
export const desktopExtent = [
  1949079.6237, 6109192.5713, 4235079.626, 8211042.5734,
];
